import { useState } from 'react';
import { useParams } from 'react-router';

import { Badge } from '../../design-system/atoms/Badge';
import { Button } from '../../design-system/atoms/Button';
import { Grid } from '../../design-system/atoms/Grid';
import { Heading } from '../../design-system/atoms/Heading';
import { Price } from '../../design-system/atoms/Price';
import { Stack } from '../../design-system/atoms/Stack';
import { Swatch } from '../../design-system/atoms/Swatch';
import { Text } from '../../design-system/atoms/Text';
import { TextLink } from '../../design-system/atoms/TextLink';
import { ProductLayout } from '../../design-system/layouts/ProductLayout';
import { Section } from '../../design-system/layouts/Section';
import { Breadcrumbs } from '../../design-system/molecules/Breadcrumbs';
import { PickupConfigurator } from '../../design-system/molecules/PickupConfigurator';
import { Disclosure } from '../../design-system/molecules/Disclosure';
import { ProductGallery } from '../../design-system/molecules/ProductGallery';
import { ArticleGrid } from '../../design-system/organisms/ArticleGrid';
import { ProductGrid } from '../../design-system/organisms/ProductGrid';
import { useCart } from '../../cart/CartContext';
import { getArticlesFeaturing } from '../../data/articles';
import { bobbinColorLabel } from '../../data/bobbinColors';
import { availableBobbinColors } from '../../data/bobbins';
import {
  cartLineKey,
  defaultConfig,
  hasConfigChoices,
  type PickupConfig,
} from '../../data/pickupConfig';
import {
  choiceLabel,
  conductorLabel,
  formatCoverOffer,
  formatSpacing,
  polepieceLabel,
  pottingLabel,
} from '../../data/pickupLabels';
import {
  getPickupAndParent,
  getRelatedPickups,
  type Choice,
  type Pickup,
  type PickupMagnet,
  type PickupPosition,
  type PickupSevenString,
  type PickupType,
} from '../../data/pickups';
import { productCrumbs } from '../../seo/breadcrumbs';
import styles from './ProductPage.module.css';

const TYPE_LABEL: Record<PickupType, string> = {
  humbucker: 'Humbucker',
  single: 'Single Coil',
  p90: 'P-90',
};

const MAGNET_LABEL: Record<PickupMagnet, string> = {
  'alnico-2': 'Alnico 2',
  'alnico-3': 'Alnico 3',
  'alnico-4': 'Alnico 4',
  'alnico-5': 'Alnico 5',
  'alnico-8': 'Alnico 8',
  ceramic: 'Ceramic',
  neodymium: 'Neodymium',
};

const POSITION_ORDER: readonly PickupPosition[] = ['neck', 'middle', 'bridge'];

function formatSevenString(sevenString: PickupSevenString | undefined): string | undefined {
  if (sevenString === undefined) return undefined;
  return `Available (${sevenString.colors.map(bobbinColorLabel).join(', ').toLowerCase()} only)`;
}

/**
 * On the base (parent) view, if the calibrated neck/bridge variants use
 * different magnets, list them per position (e.g. "Alnico 3 (neck) · Alnico 4
 * (bridge)"). Otherwise show the single magnet.
 */
function formatMagnet(active: Pickup, parent: Pickup): string {
  const isBase = active.slug === parent.slug;
  const variants = parent.variants ?? [];

  if (isBase && variants.length > 0) {
    const positional = variants
      .map((variant) => ({
        magnet: variant.magnet,
        position: variant.positions.length === 1 ? variant.positions[0] : undefined,
      }))
      .filter(
        (entry): entry is { magnet: PickupMagnet; position: PickupPosition } =>
          entry.position !== undefined,
      );

    const distinctMagnets = new Set(positional.map((entry) => entry.magnet));

    if (positional.length > 0 && distinctMagnets.size > 1) {
      return positional
        .slice()
        .sort((a, b) => POSITION_ORDER.indexOf(a.position) - POSITION_ORDER.indexOf(b.position))
        .map((entry) => `${MAGNET_LABEL[entry.magnet]} (${entry.position})`)
        .join(' · ');
    }
  }

  return MAGNET_LABEL[active.magnet];
}

/**
 * Like {@link formatMagnet}: on the base view of a set, show string spacing per
 * position (e.g. "50 mm (neck) · 52 mm (bridge)") when the variants differ, or a
 * single value when they match. Variant/single pages show their own spacing.
 * Returns undefined when no spacing is known.
 */
function formatSpacingRow(active: Pickup, parent: Pickup): string | undefined {
  const isBase = active.slug === parent.slug;
  const variants = parent.variants ?? [];

  if (isBase && variants.length > 0) {
    const positional = variants
      .map((variant) => ({
        spacing: variant.hardware.spacingMm,
        position: variant.positions.length === 1 ? variant.positions[0] : undefined,
      }))
      .filter(
        (entry): entry is { spacing: number | Choice<number>; position: PickupPosition } =>
          entry.position !== undefined && entry.spacing !== undefined,
      );

    const first = positional[0];
    if (first !== undefined) {
      const distinct = new Set(positional.map((entry) => formatSpacing(entry.spacing)));
      if (distinct.size > 1) {
        return positional
          .slice()
          .sort((a, b) => POSITION_ORDER.indexOf(a.position) - POSITION_ORDER.indexOf(b.position))
          .map((entry) => `${formatSpacing(entry.spacing)} (${entry.position})`)
          .join(' · ');
      }
      return formatSpacing(first.spacing);
    }
  }

  return active.hardware.spacingMm !== undefined
    ? formatSpacing(active.hardware.spacingMm)
    : undefined;
}

function buildGalleryImages(pickup: Pickup) {
  const main = { src: pickup.images.main, alt: pickup.name };
  const extras = (pickup.images.gallery ?? []).map((src) => ({
    src,
    alt: pickup.name,
  }));
  return [main, ...extras];
}

function specTestId(label: string): string {
  return `spec-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles['specRow']} data-testid={specTestId(label)}>
      <Text variant="label" tone="muted">
        {label}
      </Text>
      <Text variant="body" data-testid={`${specTestId(label)}-value`}>
        {value}
      </Text>
    </div>
  );
}

function SwatchRow({ label, colors }: { label: string; colors: readonly string[] }) {
  return (
    <div className={styles['specRow']} data-testid={specTestId(label)}>
      <Text variant="label" tone="muted">
        {label}
      </Text>
      <Stack direction="row" gap="xs" wrap>
        {colors.map((color) => (
          <Swatch
            key={color}
            color={color}
            label={bobbinColorLabel(color)}
            size="sm"
            data-testid={`${specTestId(label)}-${color}`}
          />
        ))}
      </Stack>
    </div>
  );
}

interface VariantSelectorProps {
  parent: Pickup;
  active: Pickup;
}

function VariantSelector({ parent, active }: VariantSelectorProps) {
  if (parent.variants === undefined || parent.variants.length === 0) return null;
  const options: readonly Pickup[] = [parent, ...parent.variants];
  const isBase = active.slug === parent.slug;

  return (
    <Stack direction="column" gap="sm" align="stretch" data-testid="product-variants">
      <Heading level={2} variant="section">
        Variants
      </Heading>
      {isBase && (
        <Text variant="meta" tone="muted">
          Choose a position (neck or bridge) to add it to your enquiry.
        </Text>
      )}
      <div className={styles['variantRow']}>
        {options.map((option) => {
          const isActive = option.slug === active.slug;
          return (
            <Button
              key={option.slug}
              linkTo={`/products/${option.slug}`}
              linkState={{ preserveScroll: true }}
              variant={isActive ? 'primary' : 'ghost'}
              size="sm"
              data-testid={`variant-link-${option.slug}`}
            >
              {option.slug === parent.slug ? 'Base' : option.name.replace(`${parent.name} · `, '')}
            </Button>
          );
        })}
      </div>
    </Stack>
  );
}

/**
 * The set page is the URL search engines rank (variant pages canonicalise to
 * it), so it carries each position's full voicing copy, a short spec line and a
 * text link to that pickup.
 */
function PositionDetails({ parent }: { parent: Pickup }) {
  const variants = parent.variants ?? [];
  if (variants.length === 0) return null;

  return (
    <Stack as="section" direction="column" gap="lg" align="stretch" data-testid="product-positions">
      <Heading level={2} variant="section" align="center">
        Neck &amp; bridge
      </Heading>
      <Grid columns={2} gap="xl" align="start">
        {variants.map((variant) => {
          const position = variant.positions.length === 1 ? variant.positions[0] : undefined;
          const summary = [
            MAGNET_LABEL[variant.magnet],
            variant.specs.dcr !== undefined ? `DCR ${variant.specs.dcr}` : undefined,
            variant.specs.inductance,
          ]
            .filter((part): part is string => part !== undefined)
            .join(' · ');

          return (
            <Stack
              key={variant.slug}
              direction="column"
              gap="sm"
              align="start"
              data-testid={`position-${variant.slug}`}
            >
              <Heading level={3} variant="editorial">
                {variant.name}
              </Heading>
              <Text variant="label" tone="gold">
                {summary}
              </Text>
              <Text variant="body">{variant.description}</Text>
              <TextLink
                to={`/products/${variant.slug}`}
                data-testid={`position-link-${variant.slug}`}
              >
                View the {position ?? variant.name} pickup <span aria-hidden="true">→</span>
              </TextLink>
            </Stack>
          );
        })}
      </Grid>
    </Stack>
  );
}

/**
 * Build configuration + the Add-to-enquiry button for an addable pickup. Owns
 * the selection (seeded from the pickup's defaults); rendered with a
 * `key={slug}` so switching variants reseeds to the new pickup's defaults. The
 * "Configure" section only appears when there is a real choice to make; the
 * complete build travels onto the cart line either way (so emails list it).
 */
function AddToEnquiry({ pickup }: { pickup: Pickup }) {
  const cart = useCart();
  const [selection, setSelection] = useState<PickupConfig>(() => defaultConfig(pickup));
  const lineId = cartLineKey(pickup.slug, selection);
  const inEnquiry = cart.items.find((item) => item.id === lineId)?.qty ?? 0;

  return (
    <>
      {hasConfigChoices(pickup) && (
        <Disclosure
          title="Configure"
          desktop="heading"
          headingLevel={2}
          data-testid="product-configure"
        >
          <PickupConfigurator
            pickup={pickup}
            value={selection}
            onChange={setSelection}
            legend={`${pickup.name} build`}
            helpTo="/faq#option-availability"
          />
        </Disclosure>
      )}
      <Button
        variant="solid"
        size="lg"
        data-testid="add-to-cart"
        onClick={() => {
          cart.add({
            slug: pickup.slug,
            name: pickup.name,
            price: pickup.price,
            config: selection,
          });
        }}
      >
        {inEnquiry > 0 ? `Added to enquiry (${String(inEnquiry)})` : 'Add to enquiry'}
      </Button>
    </>
  );
}

export default function ProductPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const found = getPickupAndParent(slug);

  if (found === undefined) {
    return (
      <Section spacing="lg" maxWidth="narrow" data-testid="product-not-found">
        <Stack direction="column" gap="md" align="center">
          <Heading level={1} variant="display" align="center">
            Pickup not found
          </Heading>
          <Text variant="editorial" tone="muted" align="center">
            We couldn&rsquo;t find a pickup matching that URL. Try the shop.
          </Text>
          <Button linkTo="/shop" variant="primary" size="md" data-testid="product-not-found-link">
            Shop pickups
          </Button>
        </Stack>
      </Section>
    );
  }

  const { pickup, parent } = found;
  const images = buildGalleryImages(pickup);
  const sevenStringLabel = formatSevenString(pickup.hardware.sevenString);
  const spacingRow = formatSpacingRow(pickup, parent);
  // The base view of a set (neck + bridge variants) is ambiguous — the buyer
  // must choose a position before it can go into the enquiry.
  const isBaseWithVariants = pickup.slug === parent.slug && (parent.variants?.length ?? 0) > 0;

  // Variant pages share their set's related pickups and articles.
  const related = getRelatedPickups(parent);
  const furtherReading = getArticlesFeaturing(parent.slug);
  const crumbs = productCrumbs(pickup, parent).map((crumb) => ({
    label: crumb.name,
    to: crumb.path,
  }));

  return (
    <Section spacing="lg" maxWidth="default" data-testid="product-page">
      <Stack direction="column" gap="xl" align="stretch">
        <Breadcrumbs items={crumbs} />
        <ProductLayout
          data-testid="product-layout"
          gallery={<ProductGallery images={images} productName={pickup.name} />}
          details={
            <Stack direction="column" gap="lg" align="stretch">
              <Stack direction="column" gap="xs" align="start">
                <Text variant="label" tone="gold" data-testid="product-type">
                  {TYPE_LABEL[pickup.type]}
                </Text>
                <Heading level={1} variant="display" align="start" data-testid="product-title">
                  {pickup.name}
                </Heading>
                <Stack direction="row" gap="sm" align="center" wrap>
                  <Price
                    amount={pickup.price}
                    size="lg"
                    tone="primary"
                    data-testid="product-price"
                  />
                  <Stack direction="row" gap="xs" wrap>
                    {pickup.positions.map((position) => (
                      <Badge
                        key={position}
                        variant="outline"
                        tone="gold"
                        size="sm"
                        data-testid={`product-position-${position}`}
                      >
                        {position}
                      </Badge>
                    ))}
                  </Stack>
                </Stack>
              </Stack>
              <Text variant="body" data-testid="product-description">
                {pickup.description}
              </Text>
              <VariantSelector parent={parent} active={pickup} />
              <Disclosure
                title="Specifications"
                desktop="heading"
                headingLevel={2}
                data-testid="product-specs"
              >
                <div className={styles['specs']}>
                  <SpecRow label="Type" value={TYPE_LABEL[pickup.type]} />
                  <SpecRow label="Magnet" value={formatMagnet(pickup, parent)} />
                  <SpecRow label="Positions" value={pickup.positions.join(', ')} />
                  <SpecRow
                    label="Lead wire"
                    value={choiceLabel(pickup.conductors, conductorLabel)}
                  />
                  {pickup.specs.dcr !== undefined && (
                    <SpecRow label="DCR" value={pickup.specs.dcr} />
                  )}
                  {pickup.specs.inductance !== undefined && (
                    <SpecRow label="Inductance" value={pickup.specs.inductance} />
                  )}
                  {pickup.specs.selfResonantPeak !== undefined && (
                    <SpecRow label="Self-resonant peak" value={pickup.specs.selfResonantPeak} />
                  )}
                  {pickup.specs.loadedResonantPeak !== undefined && (
                    <SpecRow label="Loaded resonant peak" value={pickup.specs.loadedResonantPeak} />
                  )}
                  <SpecRow label="Potting" value={choiceLabel(pickup.potting, pottingLabel)} />
                  {spacingRow !== undefined && (
                    <SpecRow label="String spacing" value={spacingRow} />
                  )}
                  <SpecRow
                    label="Pole pieces"
                    value={choiceLabel(pickup.hardware.polepieces, polepieceLabel)}
                  />
                  <SpecRow label="Cover" value={formatCoverOffer(pickup.hardware.cover)} />
                  {sevenStringLabel !== undefined && (
                    <SpecRow label="7-string" value={sevenStringLabel} />
                  )}
                  <SwatchRow
                    label="Bobbin colours"
                    colors={availableBobbinColors(pickup.hardware)}
                  />
                </div>
              </Disclosure>
              {!isBaseWithVariants && <AddToEnquiry pickup={pickup} key={pickup.slug} />}
            </Stack>
          }
        />
        {isBaseWithVariants && <PositionDetails parent={parent} />}
        {related.length > 0 && (
          <ProductGrid
            pickups={related}
            eyebrow="Related pickups"
            title="More from the bench"
            data-testid="product-related"
          />
        )}
        {furtherReading.length > 0 && (
          <ArticleGrid
            articles={furtherReading}
            eyebrow="From the workshop"
            title="Further reading"
            data-testid="product-reading"
          />
        )}
      </Stack>
    </Section>
  );
}
